/**
 * API client.
 *
 * Handles token storage, automatic refresh on 401, and the platform's error
 * envelope. A single in-flight refresh is shared across concurrent requests so
 * a page that fires six queries at once does not rotate the refresh token six
 * times — which the backend would treat as token reuse and revoke the family.
 */

const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const ACCESS_KEY = 'glee.access_token';
const REFRESH_KEY = 'glee.refresh_token';
const USER_KEY = 'glee.user';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  actor: 'platform' | 'client';
  tenant_id: string | null;
  permissions: string[];
}

export interface Paginated<T> {
  data: T[];
  page: number;
  page_size: number;
  total: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Per-field validation messages, when the server sent any.
   *
   * A 422 from FastAPI carries `details: [{field, message}]` naming exactly
   * what was wrong. Showing only `message` — a flat "Request validation
   * failed" — throws that away and leaves the user guessing which of a dozen
   * inputs the server objected to.
   */
  get fieldErrors(): Array<{ field: string; message: string }> {
    if (!Array.isArray(this.details)) return [];
    return this.details.flatMap((entry) => {
      if (entry && typeof entry === 'object' && 'message' in entry) {
        const record = entry as { field?: unknown; message?: unknown };
        return [
          {
            field: typeof record.field === 'string' ? record.field : '',
            message: String(record.message ?? ''),
          },
        ];
      }
      return [];
    });
  }
}

/** A label a person recognises, from the field name the API used. */
function humanise(field: string): string {
  if (!field) return '';
  const label = field.split('.').pop() ?? field;
  return label.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/**
 * The most useful sentence available for an error, for display in a form.
 *
 * Prefers the server's per-field messages over its summary, because
 * "First admin password: String should have at least 12 characters" is
 * actionable and "Request validation failed" is not.
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : fallback;
  }
  const fields = error.fieldErrors;
  if (fields.length === 0) return error.message || fallback;
  return fields
    .map(({ field, message }) => (field ? `${humanise(field)}: ${message}` : message))
    .join('. ');
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  user(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },
  set(access: string, refresh: string, user: AuthUser) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

type Listener = () => void;
const logoutListeners = new Set<Listener>();

export function onForcedLogout(listener: Listener): () => void {
  logoutListeners.add(listener);
  return () => logoutListeners.delete(listener);
}

function forceLogout() {
  tokens.clear();
  logoutListeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Refresh coordination
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // Share one refresh across concurrent 401s: the backend rotates refresh
  // tokens and treats a replayed one as theft, revoking the whole family.
  if (refreshInFlight) return refreshInFlight;

  const refresh = tokens.refresh();
  if (!refresh) return null;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!response.ok) {
        forceLogout();
        return null;
      }
      const body = await response.json();
      tokens.set(body.access_token, body.refresh_token, body.user);
      return body.access_token as string;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the Authorization header (login, refresh). */
  anonymous?: boolean;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(
    `${API_URL}${path.startsWith('/') ? path : `/${path}`}`,
    window.location.origin,
  );
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, query, anonymous, _retried, headers, ...rest } = options;

  const requestHeaders: Record<string, string> = {
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...((headers as Record<string, string>) ?? {}),
  };
  if (!anonymous) {
    const token = tokens.access();
    if (token) requestHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && !anonymous && !_retried) {
    const fresh = await refreshAccessToken();
    if (fresh) return request<T>(path, { ...options, _retried: true });
    forceLogout();
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(response.status, 'http_error', response.statusText);
    }
    return (await response.text()) as T;
  }

  const payload = await response.json();
  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? 'error',
      error.message ?? response.statusText,
      error.details,
    );
  }
  return payload as T;
}

/**
 * Fetch a binary response, with the same auth and refresh behaviour as `request`.
 *
 * `request` decodes anything that is not JSON with `response.text()`, which
 * mangles audio: the bytes come back through a UTF-8 decode and every
 * unrepresentable sequence becomes U+FFFD. A separate path is not duplication,
 * it is the difference between a playable recording and noise.
 */
export async function requestBlob(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  // `body` is pulled out and discarded: this is a GET, and leaving it in
  // `rest` makes the spread fail to type against RequestInit.
  const { body: _body, query, anonymous, _retried, headers, ...rest } = options;

  const requestHeaders: Record<string, string> = {
    ...((headers as Record<string, string>) ?? {}),
  };
  if (!anonymous) {
    const token = tokens.access();
    if (token) requestHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    method: 'GET',
    headers: requestHeaders,
  });

  if (response.status === 401 && !anonymous && !_retried) {
    const fresh = await refreshAccessToken();
    if (fresh) return requestBlob(path, { ...options, _retried: true });
    forceLogout();
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'http_error', response.statusText);
  }

  return response.blob();
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, query }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  getBlob: (path: string, query?: RequestOptions['query']) =>
    requestBlob(path, { query }),

  async login(email: string, password: string) {
    const body = await request<{
      access_token: string;
      refresh_token: string;
      user: AuthUser;
    }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    tokens.set(body.access_token, body.refresh_token, body.user);
    return body.user;
  },

  async logout() {
    const refresh = tokens.refresh();
    try {
      if (refresh) {
        await request('/auth/logout', {
          method: 'POST',
          body: { refresh_token: refresh },
        });
      }
    } finally {
      tokens.clear();
    }
  },

  /** Download a file response (CSV export) without losing auth. */
  async download(path: string, query?: RequestOptions['query']) {
    const token = tokens.access();
    const response = await fetch(buildUrl(path, query), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'download_failed', 'Export failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = match?.[1] ?? 'export.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

export { API_URL };
